/**
 * offlineApi.js — small axios wrappers that:
 *   • cachedGet(url, token, opts)        — try network first, fall
 *     back to IDB cache; on success, refresh the cache.
 *   • queuedMutate(method, url, body, token, opts) — try network;
 *     on offline/network error, queue in IDB and resolve optimistically
 *     so the UI can move on.
 *
 * These are intentionally axios-free for the queued path so they
 * survive page reloads and service-worker drains identically.
 */
import axios from 'axios';
import { readCache, writeCache } from './offlineCache';
import { enqueueMutation, drainMutations } from './offlineMutationQueue';
import { drainQueue as drainAudioChunks } from './chunkUploader';

export async function cachedGet(url, token, { ttlMs, signal } = {}) {
  // 1) Try network first — keeps fresh data flowing whenever possible.
  try {
    const res = await axios.get(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal,
      timeout: 20000,
    });
    // best-effort cache write; never blocks the network path
    writeCache(token, url, res.data, ttlMs ? { ttlMs } : {});
    return { data: res.data, fromCache: false, cachedAt: null };
  } catch (err) {
    // 2) Network failure (or HTTP error). Fall back to cache only when
    //    the failure looks network-y. HTTP 4xx/5xx with a real response
    //    must propagate so callers see auth errors etc.
    const isNetworky =
      !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' ||
      err.message === 'Network Error' || (typeof navigator !== 'undefined' && !navigator.onLine);
    if (!isNetworky) throw err;
    const cached = await readCache(token, url);
    if (cached) {
      return { data: cached.data, fromCache: true, cachedAt: cached.cachedAt };
    }
    throw err;
  }
}

export async function queuedMutate(method, url, body, token, { label } = {}) {
  const upper = (method || 'POST').toUpperCase();
  try {
    const res = await axios.request({
      method: upper,
      url,
      data: body,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 20000,
    });
    return { data: res.data, queued: false };
  } catch (err) {
    const isNetworky =
      !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' ||
      err.message === 'Network Error' || (typeof navigator !== 'undefined' && !navigator.onLine);
    if (!isNetworky) throw err;
    await enqueueMutation({ method: upper, url, body, token, label });
    // Best-effort: ask the SW to wake us when connectivity returns.
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.sync.register('drain-mutations');
      } catch { /* sync unsupported (Safari) — drain on `online` event still works */ }
    }
    return { data: null, queued: true };
  }
}

// Single entry point for "everything that's queued, try to upload now".
// Wired to: `online` event, app load, and service-worker DRAIN messages.
export async function drainAll(apiBase, { onChange } = {}) {
  const [chunks, muts] = await Promise.all([
    drainAudioChunks(apiBase, { onChange }),
    drainMutations({ onChange }),
  ]);
  return { chunks, mutations: muts };
}
